// "You vs. Past You" — re-attempt a paper and beat your prior best. Read-only over
// the challenge API; re-attempt reuses POST /student-papers. Additive page.
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { Button, Card, StudentPage, StudentPageHeader } from '@/components/ds';

interface Best {
  mastered: number;
  earnedMarks: number;
  fastestSeconds: number;
}
interface ChallengePaper {
  paperId: string;
  title: string;
  mode: string;
  examYear: number | null;
  totalMarks: number;
  questionCount: number;
  attempts: number;
  submittedAttempts: number;
  best: Best | null;
}

function fmtTime(s: number): string {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m ? `${m}m ${sec}s` : `${sec}s`;
}

export function ChallengePage() {
  const navigate = useNavigate();
  const papers = useQuery({
    queryKey: ['challenge', 'papers'],
    queryFn: () => apiGet<ChallengePaper[]>('/challenge/papers'),
  });

  const reattempt = useMutation({
    mutationFn: (paperId: string) => apiPost<{ id: string }>('/student-papers', { paperId }),
    onSuccess: (sp) => navigate(`/exam/${sp.id}`),
  });

  return (
    <StudentPage>
      <StudentPageHeader
        title="Compete with yourself"
        subtitle="Re-take a paper and beat your last run — more green, fewer pinks, faster. Your only rival is past you."
      />

      {papers.isLoading && <p className="text-ink-soft">Loading your papers…</p>}

      {papers.data && papers.data.length === 0 && (
        <Card>
          <div className="flex flex-col items-start gap-3 p-1">
            <p className="text-ink">
              No attempts yet. Take your diagnostic or a past paper, then come back to beat it.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/tests/diagnostic')}>Take the diagnostic →</Button>
              <Button variant="ghost" onClick={() => navigate('/tests/past-papers')}>
                Browse past papers
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {papers.data?.map((p) => (
          <Card key={p.paperId}>
            <div className="flex flex-wrap items-center justify-between gap-3 p-1">
              <div className="min-w-0">
                <div className="truncate text-base font-bold text-ink">{p.title}</div>
                <div className="mt-1 text-xs text-ink-soft" data-numeric="">
                  {p.attempts} attempt{p.attempts === 1 ? '' : 's'} · {p.questionCount} questions
                </div>
              </div>
              <div className="flex items-center gap-5">
                {p.best ? (
                  <div className="text-right">
                    <div className="text-xs font-semibold text-ink-soft">Your best</div>
                    <div className="text-sm font-bold text-forest" data-numeric="">
                      {p.best.mastered}/{p.questionCount} mastered ·{' '}
                      {fmtTime(p.best.fastestSeconds)}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-ink-faint">Not finished yet</div>
                )}
                <Button disabled={reattempt.isPending} onClick={() => reattempt.mutate(p.paperId)}>
                  {p.best ? 'Beat your best →' : 'Re-attempt →'}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {reattempt.isError && (
        <p className="text-sm text-danger">Couldn’t start the paper. Try again.</p>
      )}
    </StudentPage>
  );
}
